import { create } from 'zustand'
import { auth, setAuthToken, type User } from '../lib/api'

const TOKEN_KEY = 'videolab_token'

interface AuthState {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  /** Capture JWT from the URL hash after magic-link redirect */
  handleAuthCallback: () => boolean
  fetchUser: () => Promise<void>
  logout: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,

  handleAuthCallback: () => {
    const hash = window.location.hash
    if (hash.startsWith('#token=')) {
      const token = hash.slice(7)
      sessionStorage.setItem(TOKEN_KEY, token)
      setAuthToken(token)
      // Clear the hash from the URL without reloading
      window.history.replaceState(null, '', window.location.pathname)
      return true
    }
    // Also restore from sessionStorage on page reload
    const stored = sessionStorage.getItem(TOKEN_KEY)
    if (stored) {
      setAuthToken(stored)
      return true
    }
    return false
  },

  fetchUser: async () => {
    set({ isLoading: true })
    try {
      const res = await auth.getCurrentUser()
      // API returns { user: { ... } }
      const user = (res as unknown as { user: User }).user ?? res
      set({ user, isAuthenticated: true, isLoading: false })
    } catch {
      // Token is invalid or expired — clear it
      sessionStorage.removeItem(TOKEN_KEY)
      setAuthToken(null)
      set({ user: null, isAuthenticated: false, isLoading: false })
    }
  },

  logout: async () => {
    try {
      await auth.logout()
    } catch {
      // ignore errors on logout
    } finally {
      sessionStorage.removeItem(TOKEN_KEY)
      setAuthToken(null)
      set({ user: null, isAuthenticated: false })
    }
  },
}))
